import { DEFAULT_DIALOG_CONFIG } from '@angular/cdk/dialog';
import {
  FullscreenOverlayContainer,
  OVERLAY_DEFAULT_CONFIG,
  OverlayContainer,
} from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, Router, withInMemoryScrolling } from '@angular/router';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import * as Sentry from '@sentry/angular';
import {
  provideTanStackQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';

import { environment } from '../environments/environment';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    // In dev, render CDK overlays with classic z-index stacking instead of
    // the native Popover API. Popover overlays live in the browser's top
    // layer, which paints above everything and makes the dev widgets
    // (Agentation toolbar, TanStack devtools) unusable while a dialog is open.
    ...(environment.production
      ? []
      : [{ provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } }]),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
    ),
    {
      provide: DatePipe,
    },
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    provideHotToastConfig(),
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
          },
          mutations: {
            retry: 1,
          },
        },
      }),
      withDevtools(),
    ),
    {
      provide: DEFAULT_DIALOG_CONFIG,
      useValue: {
        hasBackdrop: true,
        panelClass: 'dialog',
        padding: '1.5rem',
        backdropClass: 'dialog-backdrop',
        closeOnNavigation: true,
      },
    },

    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },

    provideAppInitializer(() => {
      inject(Sentry.TraceService);
      return;
    }),
  ],
};
